import { IWebServiceManager } from './IWebServiceManager';
import { IWebServiceRequest } from './IWebServiceRequest';
import { IUserContext } from './../IUserContext';
import { HeaderList } from './HeaderList';
import { HttpMethod } from './HttpMethod';
import { ContentType } from './ContentType';
import { IRequestOptions } from './IRequestOptions';
import { IRequestData } from './IRequestData';

import { WebServiceRequest } from './WebServiceRequest';

export class WebServiceManager implements IWebServiceManager {
    constructor(context: IUserContext) {
        this.context = context;
        this.main = 'main';
        this.api = 'api';
    }

    private context: IUserContext;

    private isOptions<T>(data: T | IRequestData<T>): data is IRequestData<T> {
        return (data as IRequestData<T>).content !== undefined;
    }

    private getHeaders(options: IRequestOptions, contentType: string | null): HeaderList {
        let headers = options.headers;
        if (!headers || headers === null) {
            headers = new HeaderList();
        }
        headers["X-Requested-With"] = "XMLHttpRequest";
        if (contentType && contentType != null) {
            headers["Content-Type"] = contentType;
        }

        var context = this.context;
        if (!options.skipServiceHeader && context.services.has(options.service)) {
            headers["X-TD-GW-SITE"] = context.sys;
            headers["Request-Id"] = context.id;

            if (context.did && context.did.length > 0) {
                headers["X-TD-DID"] = context.did;
            }

            var adId = context.getSetting('ad');
            var session = context.getSetting('session');
            var referrer = context.getSetting('referrer');
            var address = context.getSetting('address');

            if (adId !== null) {
                headers["X-TD-AD"] = adId;
            }
            if (session !== null) {
                headers["X-TD-SESSION"] = session;
            }
            if (referrer !== null) {
                headers["Referer"] = referrer;
            }
            if (address !== null) {
                headers["X-TD-ADDRESS"] = address;
            }

            var apiKey = context.getClaimValue('apikey');
            if (apiKey !== null) {
                headers["X-TD-APIKEY"] = apiKey;
            }

            if (context.auth && context.auth !== null && context.auth.length > 0) {
                headers["Authorization"] = 'Bearer ' + context.auth;
            }
        }

        return headers;
    }

    private getJsonContent(data: any): string | null {
        if (!data || data == null) {
            return null;
        }
        if (typeof data === 'string') {
            return data;
        }

        return JSON.stringify(data);
    }

    private getFormContent(data: any): string | null {
        if (!data || data == null) {
            return null;
        }
        if (typeof data === 'string') {
            return data as string;
        }

        let keys = Object.keys(data);
        let values = Object.values(data);

        let items: string[] = [];
        for (let i = 0; i < keys.length; i++) {
            let valueContent = this.getValueContent(values[i], keys[i], '');
            for (let i = 0; i < valueContent.length; i++) {
                items.push(valueContent[i]);
            }
        }
        return items.join('&');
    }

    private getValueContent(data: any, key: string, prefix: string): string[] {
        if (!data || data == null) {
            return [prefix + key + '='];
        }
        if (typeof data === 'string') {
            return [prefix + key + '=' + data];
        }

        let vals: string[] = [];
        let keys = Object.keys(data);
        let values = Object.values(data);
        for (let i = 0; i < keys.length; i++) {
            let items = this.getValueContent(values[i], keys[i], prefix + key + '.');
            for (let i = 0; i < items.length; i++) {
                vals.push(items[i]);
            }
        }
        return vals;
    }

    readonly main: string;
    readonly api: string;

    createRequest<T>(options: IRequestOptions, data?: T | IRequestData<T>): IWebServiceRequest {
        let content: string | null = null;
        let contentType: string | null = null;
        if (data) {
            if (this.isOptions(data)) {
                contentType = data.contentType ?? null;
                switch (data.type) {
                    case ContentType.FORM:
                        if (!contentType || contentType === null) {
                            contentType = 'application/x-www-form-urlencoded';
                        }
                        content = this.getFormContent(data.content);
                        break;

                    case ContentType.STRING:
                        if (!contentType || contentType === null) {
                            contentType = 'text/plain';
                        }
                        content = this.getJsonContent(data.content);
                        break;

                    default:
                        if (!contentType || contentType === null) {
                            contentType = 'application/json';
                        }
                        content = this.getJsonContent(data.content);
                        break;
                }
            } else {
                contentType = 'application/json';
                content = this.getJsonContent(data);
                if (typeof data === 'string') {
                    contentType = 'text/plain';
                }
            }
        }

        var url = this.context.getServiceUrl(options.service, options.path);
        var headers = this.getHeaders(options, contentType);
        return new WebServiceRequest(url, options.method, content, headers);
    }

    send(request: IWebServiceRequest): Promise<Response> {
        console.debug('sending fetch request', request);
        return fetch(request.url, {
            method: request.method,
            headers: request.headers,
            body: request.content
        });
    }

    get<T>(request: IWebServiceRequest): Promise<T> {
        return this.send(request)
            .then(res => {
                if (!res.ok) {
                    let msg = 'Request failed: (' + res.status + ') ' + res.statusText;
                    throw new Error(msg);
                }

                return res.json() as Promise<T>;
            });
    }

    getString(request: IWebServiceRequest): Promise<string> {
        return this.send(request)
            .then(res => {
                if (!res.ok) {
                    let msg = 'Request failed: (' + res.status + ') ' + res.statusText;
                    throw new Error(msg);
                }

                return res.text();
            });
    }
}